import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Photo, Artist, Collection } from '../types';

interface GalleryContextType {
  photos: Photo[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  favorites: any[];
  collections: Collection[];
  artists: Artist[];
  artistsLoading: boolean;
  artistsError: string | null;
  viewMode: string;
  setViewMode: (mode: string) => void;
  toggleFavorite: (photo: any) => void;
  addToCollection: (collectionId: string, photo: any) => void;
  createCollection: (name: string, description?: string) => void;
  removeFromCollection: (collectionId: string, photoId: string) => void;
  deleteCollection: (collectionId: string) => void;
  fetchPhotos: (pageNum?: number, query?: string, isNewSearch?: boolean) => Promise<void>;
  fetchArtists: () => Promise<void>;
  fetchArtistPhotos: (artistId: string) => Promise<Photo[]>;
  searchImages: (query: string) => void;
  loadMore: () => void;
}

const GalleryContext = createContext<GalleryContextType | undefined>(undefined);

export const useGallery = () => {
  const context = useContext(GalleryContext);
  if (!context) {
    throw new Error('useGallery must be used within a GalleryProvider');
  }
  return context;
};

export const GalleryProvider = ({ children }: { children: ReactNode }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('masonry');

  const [favorites, setFavorites] = useState<any[]>(() => {
    const savedFavorites = localStorage.getItem('favorites');
    return savedFavorites ? JSON.parse(savedFavorites) : [];
  });
  const [collections, setCollections] = useState<Collection[]>(() => {
    const savedCollections = localStorage.getItem('collections');
    return savedCollections ? JSON.parse(savedCollections) : [];
  });
  const [artists, setArtists] = useState<Artist[]>([]);
  const [artistsLoading, setArtistsLoading] = useState(true);
  const [artistsError, setArtistsError] = useState<string | null>(null);

  const clientID = "XkVnwbvPiS57VJf_jho_Z16b7G-DdXCfR9ZQgxmEmAE";

  // Helper to transform Unsplash data
  const transformPhoto = (photo: any): Photo => ({
    id: photo.id,
    urls: {
      thumb: photo.urls.thumb,
      small: photo.urls.small,
      regular: photo.urls.regular,
      full: photo.urls.full
    },
    alt_description: photo.alt_description,
    description: photo.description,
    likes: photo.likes,
    views: photo.views || 0,
    downloads: photo.downloads || 0,
    created_at: photo.created_at,
    user: {
      id: photo.user.id,
      name: photo.user.name,
      username: photo.user.username,
      portfolio_url: photo.user.portfolio_url,
      profile_image: {
        small: photo.user.profile_image.small,
        medium: photo.user.profile_image.medium,
        large: photo.user.profile_image.large
      }
    },
    tags: photo.tags || [],
    location: photo.location || null,
    exif: photo.exif || null,
    width: photo.width,
    height: photo.height
  });

  // Fetch photos from Unsplash API
  const fetchPhotos = useCallback(async (pageNum = 1, query = '', isNewSearch = false) => {
    try {
      if (isNewSearch || pageNum === 1) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      let url = `https://api.unsplash.com/photos?client_id=${clientID}&per_page=30&page=${pageNum}`;
      if (query) {
        url = `https://api.unsplash.com/search/photos?client_id=${clientID}&query=${query}&per_page=30&page=${pageNum}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const results = query ? data.results : data;
      const transformedPhotos = results.map(transformPhoto);

      setPhotos(prevPhotos => {
        if (isNewSearch || pageNum === 1) {
          return transformedPhotos;
        } else {
          const newIds = new Set(transformedPhotos.map((p: any) => p.id));
          return [...prevPhotos, ...transformedPhotos.filter((p: any) => !newIds.has(prevPhotos.find(existing => existing.id === p.id)))];
        }
      });
    } catch (error) {
      console.error('Error fetching photos:', error);
      if (pageNum === 1) setError('Failed to load photos. Please try again later.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Public function to load more photos
  const loadMore = useCallback(() => {
    if (!loading && !loadingMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPhotos(nextPage, searchQuery);
    }
  }, [loading, loadingMore, page, fetchPhotos, searchQuery]);

  // Public function to search
  const searchImages = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
    fetchPhotos(1, query, true);
  }, [fetchPhotos]);

  // Fetch artists from Unsplash API
  const fetchArtists = useCallback(async () => {
    try {
      setArtistsLoading(true);
      setArtistsError(null);

      const response = await fetch(`https://api.unsplash.com/users?client_id=${clientID}&per_page=30`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const transformedArtists = data.map((artist: any) => ({
        id: artist.id,
        name: artist.name,
        username: artist.username,
        portfolio_url: artist.portfolio_url,
        bio: artist.bio,
        location: artist.location,
        total_photos: artist.total_photos,
        followers_count: artist.followers_count,
        following_count: artist.following_count,
        profile_image: {
          small: artist.profile_image.small,
          medium: artist.profile_image.medium,
          large: artist.profile_image.large
        },
        instagram_username: artist.instagram_username,
        twitter_username: artist.twitter_username
      }));

      setArtists(transformedArtists);
    } catch (error) {
      console.error('Error fetching artists:', error);
      setArtistsError('Failed to load artists. Please try again later.');
    } finally {
      setArtistsLoading(false);
    }
  }, []);

  // Fetch photos for a specific artist
  const fetchArtistPhotos = useCallback(async (artistId: string) => {
    try {
      const response = await fetch(`https://api.unsplash.com/users/${artistId}/photos?client_id=${clientID}&per_page=30`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.map(transformPhoto);
    } catch (error) {
      console.error('Error fetching artist photos:', error);
      throw new Error('Failed to load artist photos. Please try again later.');
    }
  }, []);

  // Toggle favorite status for a photo
  const toggleFavorite = useCallback((photo: any) => {
    setFavorites(prevFavorites => {
      const isFavorite = prevFavorites.some(fav => fav.id === photo.id);
      let newFavorites;

      if (isFavorite) {
        newFavorites = prevFavorites.filter(fav => fav.id !== photo.id);
      } else {
        newFavorites = [...prevFavorites, photo];
      }

      localStorage.setItem('favorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  }, []);

  // Add a photo to a collection
  const addToCollection = useCallback((collectionId: string, photo: any) => {
    setCollections(prevCollections => {
      const updatedCollections = prevCollections.map(collection => {
        if (collection.id === collectionId) {
          const isPhotoInCollection = collection.photos.some(p => p.id === photo.id);
          if (!isPhotoInCollection) {
            return {
              ...collection,
              photos: [...collection.photos, photo]
            };
          }
        }
        return collection;
      });

      localStorage.setItem('collections', JSON.stringify(updatedCollections));
      return updatedCollections;
    });
  }, []);

  // Create a new collection
  const createCollection = useCallback((name: string, description = '') => {
    setCollections(prevCollections => {
      const newCollection = {
        id: Date.now().toString(),
        name,
        description,
        photos: [],
        createdAt: new Date().toISOString()
      };

      const updatedCollections = [...prevCollections, newCollection];
      localStorage.setItem('collections', JSON.stringify(updatedCollections));
      return updatedCollections;
    });
  }, []);

  // Remove a photo from a collection
  const removeFromCollection = useCallback((collectionId: string, photoId: string) => {
    setCollections(prevCollections => {
      const updatedCollections = prevCollections.map(collection => {
        if (collection.id === collectionId) {
          return {
            ...collection,
            photos: collection.photos.filter(photo => photo.id !== photoId)
          };
        }
        return collection;
      });

      localStorage.setItem('collections', JSON.stringify(updatedCollections));
      return updatedCollections;
    });
  }, []);

  // Delete a collection
  const deleteCollection = useCallback((collectionId: string) => {
    setCollections(prevCollections => {
      const updatedCollections = prevCollections.filter(collection => collection.id !== collectionId);
      localStorage.setItem('collections', JSON.stringify(updatedCollections));
      return updatedCollections;
    });
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchPhotos(1);
    fetchArtists();
  }, [fetchPhotos, fetchArtists]);

  const value = {
    photos,
    loading,
    loadingMore,
    error,
    favorites,
    collections,
    artists,
    artistsLoading,
    artistsError,
    viewMode,
    setViewMode,
    toggleFavorite,
    addToCollection,
    createCollection,
    removeFromCollection,
    deleteCollection,
    fetchPhotos,
    fetchArtists,
    fetchArtistPhotos,
    searchImages,
    loadMore
  };

  return (
    <GalleryContext.Provider value={value}>
      {children}
    </GalleryContext.Provider>
  );
};

export default GalleryContext;